/**
 * BusinessClosedDatesManager Component
 *
 * Manages individual closed dates for a business.
 * Allows adding, viewing, and removing dates when the business is closed.
 */

import React, { useState, useMemo } from 'react';
import { CalendarOff, Plus, Trash2, AlertTriangle, Info, Calendar, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/ui/use-toast';
import { format, startOfYear, endOfYear, addYears, isBefore, startOfDay } from 'date-fns';
import { useBusinessClosedDates } from '@/hooks/business/useBusinessClosedDates';
import { BusinessOpeningHour, BusinessClosedDate } from '@/types';
import { SettingsCard } from '../ui';

interface BusinessClosedDatesManagerProps {
  businessId: string | null;
  openingHours?: BusinessOpeningHour[] | null;
}

export const BusinessClosedDatesManager: React.FC<BusinessClosedDatesManagerProps> = ({
  businessId,
  openingHours
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [reason, setReason] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [existingShiftsInfo, setExistingShiftsInfo] = useState<{ count: number; hasAssignedShifts: boolean } | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Fetch closed dates for current and next year
  const dateRange = useMemo(() => ({
    start: startOfYear(new Date()),
    end: endOfYear(addYears(new Date(), 1))
  }), []);

  const {
    closedDates,
    autoCloseOnHolidays,
    loading,
    addDate,
    removeDate,
    setAutoCloseOnHolidays,
    checkExistingShifts
  } = useBusinessClosedDates({
    businessId,
    startDate: dateRange.start,
    endDate: dateRange.end,
    openingHours
  });

  // Filter to show only future closed dates
  const futureClosedDates = useMemo(() => {
    const today = startOfDay(new Date());
    return closedDates.filter(cd => !isBefore(new Date(cd.closed_date), today));
  }, [closedDates]);

  // Group closed dates by month for display
  const closedDatesByMonth = useMemo(() => {
    const grouped = new Map<string, BusinessClosedDate[]>();
    futureClosedDates.forEach(cd => {
      const monthKey = format(new Date(cd.closed_date), 'MMMM yyyy');
      if (!grouped.has(monthKey)) {
        grouped.set(monthKey, []);
      }
      grouped.get(monthKey)!.push(cd);
    });
    return grouped;
  }, [futureClosedDates]);

  const handleDateSelect = async (date: Date | undefined) => {
    if (!date || !businessId) return;

    // Check if date is in the past
    if (isBefore(date, startOfDay(new Date()))) {
      toast.error('Cannot mark past dates as closed');
      return;
    }

    setSelectedDate(date);

    // Check if date is already a closed date
    const dateStr = format(date, 'yyyy-MM-dd');
    const isAlreadyClosed = closedDates.some(cd => cd.closed_date === dateStr);
    if (isAlreadyClosed) {
      toast.error('This date is already marked as closed');
      return;
    }

    setIsCalendarOpen(false);

    // Check for existing shifts
    const shiftsInfo = await checkExistingShifts(date);
    setExistingShiftsInfo(shiftsInfo);

    if (shiftsInfo.count > 0) {
      setIsConfirmDialogOpen(true);
    } else {
      setIsAddDialogOpen(true);
    }
  };

  const handleAddClosedDate = async () => {
    if (!selectedDate) return;

    try {
      await addDate(selectedDate, reason || undefined);
      toast.success('Closed date added', {
        description: `${format(selectedDate, 'EEEE, MMMM d, yyyy')} marked as closed`
      });
      setIsAddDialogOpen(false);
      setIsConfirmDialogOpen(false);
      setSelectedDate(undefined);
      setReason('');
      setExistingShiftsInfo(null);
    } catch (error) {
      toast.error('Failed to add closed date', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  const handleRemoveClosedDate = async (cd: BusinessClosedDate) => {
    try {
      await removeDate(cd.id);
      toast.success('Closed date removed', {
        description: `${format(new Date(cd.closed_date), 'MMMM d, yyyy')} is no longer marked as closed`
      });
    } catch (error) {
      toast.error('Failed to remove closed date', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  const handleAutoCloseToggle = async (checked: boolean) => {
    try {
      await setAutoCloseOnHolidays(checked);
      toast.success(checked ? 'Auto-close on holidays enabled' : 'Auto-close on holidays disabled');
    } catch (error) {
      toast.error('Failed to update setting', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  // Dates to highlight in calendar (already marked as closed)
  const closedDateValues = useMemo(() => {
    return closedDates.map(cd => new Date(cd.closed_date));
  }, [closedDates]);

  if (!businessId) {
    return null;
  }

  return (
    <SettingsCard
      icon={CalendarOff}
      title="Custom Closed Dates"
      description="Mark individual dates when your business will be closed (beyond the regular weekly schedule)"
    >
      <div className="space-y-6">
        {/* Auto-close on holidays toggle */}
        <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-muted-foreground" />
            <div>
              <Label htmlFor="auto-close-holidays" className="text-sm font-medium">
                Auto-close on public holidays
              </Label>
              <p className="text-xs text-muted-foreground">
                Automatically mark business as closed on public holidays for your country
              </p>
            </div>
          </div>
          <Switch
            id="auto-close-holidays"
            checked={autoCloseOnHolidays}
            onCheckedChange={handleAutoCloseToggle}
          />
        </div>

        <Separator />

        {/* Add Closed Date Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Add Closed Date</Label>
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Select Date
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => isBefore(date, startOfDay(new Date()))}
                  modifiers={{
                    closed: closedDateValues
                  }}
                  modifiersStyles={{
                    closed: {
                      backgroundColor: 'hsl(var(--destructive) / 0.1)',
                      color: 'hsl(var(--destructive))',
                      fontWeight: 'bold'
                    }
                  }}
                  initialFocus
                />
                <div className="p-3 border-t">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-destructive/10 inline-block" />
                    Already marked as closed
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Existing Closed Dates List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading closed dates...</div>
          </div>
        ) : futureClosedDates.length > 0 ? (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Upcoming Closed Dates</Label>
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-4">
                {Array.from(closedDatesByMonth.entries()).map(([monthKey, dates]) => (
                  <div key={monthKey} className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {monthKey}
                    </p>
                    <div className="space-y-2">
                      {dates.map(cd => (
                        <div
                          key={cd.id}
                          className="flex items-center justify-between p-3 rounded-lg border bg-background hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <Badge className="font-medium">
                                {format(new Date(cd.closed_date), 'EEE, MMM d')}
                              </Badge>
                              {cd.reason && (
                                <p className="text-sm text-muted-foreground mt-1">{cd.reason}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveClosedDate(cd)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No custom closed dates set. Use the button above to mark specific dates when your business will be closed.
            </AlertDescription>
          </Alert>
        )}

        {/* Add Reason Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Closed Date</DialogTitle>
              <DialogDescription>
                Mark this date as closed for your business
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm">
                Mark <strong>{selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong> as closed
              </p>
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Input
                  id="reason"
                  placeholder="e.g., Holiday, Renovation, Training day..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddClosedDate}>
                Add Closed Date
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Dialog (when shifts exist) */}
        <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Existing Shifts Found
              </DialogTitle>
              <DialogDescription>
                Review the shifts scheduled for this date
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  There {existingShiftsInfo?.count === 1 ? 'is' : 'are'}{' '}
                  <strong>{existingShiftsInfo?.count} shift{existingShiftsInfo?.count !== 1 ? 's' : ''}</strong>{' '}
                  scheduled for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}.
                  {existingShiftsInfo?.hasAssignedShifts && ' Some shifts have employees assigned.'}
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                Marking this date as closed will <strong>not</strong> automatically delete existing shifts.
                You may want to review and remove them manually from the calendar.
              </p>
              <div className="space-y-2">
                <Label htmlFor="reason-confirm">Reason (optional)</Label>
                <Input
                  id="reason-confirm"
                  placeholder="e.g., Holiday, Renovation, Training day..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsConfirmDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddClosedDate} variant="default">
                Mark as Closed Anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SettingsCard>
  );
};

export default BusinessClosedDatesManager;
